<template>
  <div class="fee-history-timeline">
    <div
      v-if="!history || history.length === 0"
      class="text-center p-4 empty-state-box"
    >
      <feather-icon
        icon="ClockIcon"
        size="44"
        class="text-muted mb-2 empty-state-icon"
      />
      <h5 class="empty-state-title mb-50">{{ $t("No Fee History") }}</h5>
      <p class="empty-state-message mb-0">
        {{ $t("No fee payments or receipts recorded yet.") }}
      </p>
    </div>

    <ul v-else class="vuexy-timeline list-unstyled mb-0">
      <li
        v-for="(item, ind) in history"
        :key="ind"
        class="timeline-item"
      >
        <span
          class="timeline-point"
          :class="getPointClass(item)"
        >
          <feather-icon :icon="getPointIcon(item)" size="16" />
        </span>

        <div class="timeline-event animation-fade">
          <div
            class="d-flex justify-content-between flex-sm-row flex-column mb-sm-0 mb-1"
          >
            <div class="d-flex align-items-center flex-wrap">
              <h5
                v-if="item.fees.billID <= 0"
                class="font-weight-bolder mb-0 text-capitalize"
                :class="getTitleClass(item)"
              >
                {{ item.fees.status }}
              </h5>
              <h5
                v-else
                class="text-primary font-weight-bolder mb-0 d-flex align-items-center"
              >
                <feather-icon icon="FileTextIcon" size="16" class="mr-50" />
                {{ $t("Slip No") }}# {{ item.fees.billID }}
              </h5>
              <b-button
                v-if="item.fees.billID > 0"
                v-ripple.400="'rgba(115, 103, 240, 0.15)'"
                variant="outline-primary"
                size="sm"
                class="btn-icon ml-50 mt-50 mt-sm-0"
                v-b-tooltip.hover
                :title="$t('Print Slip')"
                @click="
                  printSlip(
                    item.fees.billID,
                    item.fees.campusID,
                    item.stdd.family_code
                  )
                "
              >
                <feather-icon icon="PrinterIcon" size="15" />
              </b-button>
            </div>
            <b-badge
              variant="light-secondary"
              class="timeline-badge align-self-start mt-50 mt-sm-0"
            >
              <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
              {{ formatDate(item.fees.rcvdDate) }}
            </b-badge>
          </div>

          <b-row class="mt-2">
            <b-col cols="12" sm="6" class="mb-1 mb-sm-0">
              <div class="history-meta-block">
                <span class="history-meta-label">{{ $t("Fee Type") }}</span>
                <span class="history-meta-value">{{ item.feesType.feeType }}</span>
              </div>
            </b-col>
            <b-col cols="12" sm="6">
              <div class="history-meta-block history-meta-block--amount text-sm-right">
                <span class="history-meta-label">{{ $t("Received") }}</span>
                <span class="history-meta-value history-meta-value--amount">
                  {{ formatAmount(item) }}
                </span>
              </div>
            </b-col>
          </b-row>

          <div
            v-if="item.fees.barcode > 0"
            class="mt-1 pt-1 border-top receipt-row"
          >
            <span class="text-muted font-small-3 text-uppercase letter-spacing">
              <feather-icon icon="HashIcon" size="12" class="mr-25" />
              {{ $t("Receipt No") }}#
            </span>
            <span class="font-weight-bold text-body ml-50">
              {{ item.fees.barcode }}
            </span>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { BButton, BBadge, BRow, BCol, VBTooltip } from "bootstrap-vue";
import Ripple from "vue-ripple-directive";

export default {
  components: {
    BButton,
    BBadge,
    BRow,
    BCol,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  props: {
    history: {
      type: Array,
      required: true,
    },
    duesTotal: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      reportDomain: this.$store.state.report.replace(/_/g, ""),
    };
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },
    getPointIcon(item) {
      if (item.fees.billID <= 0) return "SlashIcon";
      return "CheckCircleIcon";
    },
    getPointClass(item) {
      if (item.fees.billID <= 0) return "timeline-point--danger";
      return "timeline-point--success";
    },
    getTitleClass(item) {
      const status = String(item.fees.status || "").toLowerCase();
      if (status.includes("exempt") || status.includes("hold")) {
        return "text-danger";
      }
      return "text-warning";
    },
    formatAmount(item) {
      const value =
        item.fees.billID == 0 ? item.fees.exempted : item.fees.rcvd;
      if (value === null || value === undefined || value === "") return "—";
      return value;
    },
    printSlip(id, cID, fcode) {
      const url =
        `https://${this.reportDomain}.myskool.app/Fee/Slip?dbb=` +
        this.$store.state.userData.db +
        "&bID=" +
        id +
        "&cID=" +
        cID +
        "&rem=" +
        this.duesTotal +
        "&fcode=" +
        fcode;
      window.open(url, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
.fee-history-timeline {
  padding-top: 0.25rem;
  padding-left: 0.15rem;
  padding-right: 0.15rem;
  overflow: visible;
}

.vuexy-timeline {
  position: relative;
  padding-left: 1.5rem;
}

.vuexy-timeline .timeline-item {
  position: relative;
  padding-bottom: 2.5rem;
  padding-left: 2.5rem;
  border-left: 2px solid #ebe9f1;
}

.vuexy-timeline .timeline-item:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}

.vuexy-timeline .timeline-point {
  position: absolute;
  left: 0;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: #fff;
  border: 2px solid #7367f0;
  border-radius: 50%;
  color: #7367f0;
  box-shadow: 0 4px 8px rgba(115, 103, 240, 0.15);
  z-index: 2;
}

.vuexy-timeline .timeline-point.timeline-point--success {
  border-color: #28c76f;
  color: #28c76f;
  box-shadow: 0 4px 8px rgba(40, 199, 111, 0.15);
}

.vuexy-timeline .timeline-point.timeline-point--danger {
  border-color: #ea5455;
  color: #ea5455;
  box-shadow: 0 4px 8px rgba(234, 84, 85, 0.15);
}

.vuexy-timeline .timeline-event {
  background-color: #ffffff;
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease-in-out;
}

.vuexy-timeline .timeline-event:hover {
  box-shadow: 0 6px 20px rgba(115, 103, 240, 0.08);
  border-color: rgba(115, 103, 240, 0.3);
  transform: translateY(-2px);
}

.timeline-badge {
  padding: 0.4rem 0.6rem;
  font-size: 85%;
}

.letter-spacing {
  letter-spacing: 0.5px;
}

.receipt-row {
  border-color: #f0edff !important;
}

.history-meta-block {
  background: #f9fafb;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  padding: 0.65rem 0.85rem;
}

.history-meta-block--amount {
  background: #f6fff9;
  border-color: #d1fae5;
}

.history-meta-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin-bottom: 0.2rem;
}

.history-meta-value {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.35;
}

.history-meta-value--amount {
  font-size: 1.05rem;
  font-weight: 700;
  color: #16a34a;
}

.empty-state-box {
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed #e5e7eb;
}

.empty-state-icon {
  color: #cbb9ff !important;
}

.empty-state-title {
  font-weight: 700;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.empty-state-message {
  font-size: 0.95rem;
  font-weight: 500;
  color: #6b7280;
}

.animation-fade {
  animation: feeHistoryFadeIn 0.35s ease-out;
}

@keyframes feeHistoryFadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 767.98px) {
  .vuexy-timeline {
    padding-left: 1.35rem;
  }

  .vuexy-timeline .timeline-item {
    padding-left: 2rem;
    padding-bottom: 1.5rem;
  }

  .vuexy-timeline .timeline-point {
    width: 32px;
    height: 32px;
  }

  .vuexy-timeline .timeline-event {
    padding: 0.9rem 1rem;

    &:hover {
      transform: none;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
    }
  }

  .history-meta-block--amount {
    text-align: left !important;
  }

  .timeline-badge {
    font-size: 0.78rem;
    padding: 0.3rem 0.5rem;
  }
}
</style>
